import { forwardRef } from 'react';
import { cn } from '../../../lib/cn';
/* `.ds-pcard__quickadd` only. The rest of `.ds-pcard` is vendored in
   `mobile/organisms.css`; this button is an app-side addition the library has
   no equivalent for, so it lives beside its desktop twin
   (`.dsa-listing__quickadd`) rather than in a file `sync.sh` overwrites. Same
   move `Table.jsx` and `CartButton.jsx` make. */
import '../../../patterns/market/market.css';
/* Same folder, same reason as the stylesheet above: the quick-add button is an
   app-side addition with a desktop twin, so the rule that picks its words is
   shared with that twin instead of typed out twice. */
import { quickAddAccessibleName, quickAddText } from '../../../patterns/market/quickAdd';
import { HeartGlyph } from './icons';

/**
 * WishlistHeart.
 *
 * CSS selector: `organisms.css .ds-heart`.
 *
 * Doctrine: the heart is a real standalone block, not a ProductCard element.
 * 44px hit area over a 34px pill, bottom right of the tile, and the fill comes
 * from `.ds-heart[aria-pressed="true"] svg { fill: currentColor }` which is why
 * the glyph ships unfilled and the CSS does the filling. Ink is command and
 * accent is state, so a saved heart fills, it never turns accent. The one
 * exception is `.ds-confirm-strip__heart`, a momentary save confirmation.
 *
 * Rendered as a `<span role="button">` when it sits inside a ProductCard that
 * is itself a `<button>`, because a nested `<button>` is invalid HTML. Outside
 * that case it is a real `<button>`.
 */
export const WishlistHeart = forwardRef(function WishlistHeart(
  {
    className,
    pressed = false,
    label = 'Save to wishlist',
    onToggle,
    onClick,
    asSpan = false,
    ...rest
  },
  ref,
) {
  const classes = cn('ds-heart', className);

  function fire(event) {
    // The card behind the heart navigates. Stopping here is what keeps a save
    // from also being a page change.
    event.stopPropagation();
    if (onClick) onClick(event);
    if (onToggle) onToggle(!pressed, event);
  }

  if (asSpan) {
    return (
      <span
        ref={ref}
        role="button"
        tabIndex={0}
        aria-pressed={pressed}
        aria-label={label}
        className={classes}
        onClick={fire}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            fire(event);
          }
        }}
        {...rest}
      >
        <HeartGlyph />
      </span>
    );
  }

  return (
    <button
      ref={ref}
      type="button"
      aria-pressed={pressed}
      aria-label={label}
      className={classes}
      onClick={fire}
      {...rest}
    >
      <HeartGlyph />
    </button>
  );
});

const TILE_ASPECT = {
  square: null,
  '4x5': 'ds-pcard__tile--4x5',
  card: 'ds-pcard__tile--card',
};

/**
 * ProductCard, the single most important component in the app.
 *
 * CSS selector: `organisms.css .ds-pcard` plus `--mini`, the tile aspect
 * modifiers `.ds-pcard__tile--4x5` / `--card`, and the shared text anatomy in
 * `molecules.css` (`.ds-pcard__kicker` / `__title` / `__metarow` / `__code` /
 * `__listings` / `__from` / `__pricemain` / `__market` / `__market-value`,
 * aliased onto `.ds-pricestack__*` so the product card IS a price stack).
 *
 * Doctrine, all of it load bearing:
 *
 * - ONE component, three modes, everything else is show or hide. `tile` is the
 *   grid card, `row` is list view and search results, `compact` is cart, drawer
 *   and sidebar. Adding a fourth card component is the failure mode this exists
 *   to prevent.
 * - The anatomy order is FIXED: set kicker, card name, smallest meta row
 *   (rarity tag + card code), "N listings from" + main price, then Market price
 *   on its own line in success green. Availability sits on the image tile.
 * - PRICE WEIGHT IS LIGHTER THAN TITLE WEIGHT, or at most equal. The klarna-04
 *   invert. `.ds-pcard__pricemain` is 16/600 exactly like the title and the CSS
 *   comment says "NEVER heavier".
 * - NO store name on a tile. `seller` is deliberately dropped in `tile` mode
 *   and only renders in `row` / `compact`, where you are choosing between
 *   sellers rather than browsing cards.
 * - There are no PSA / BGS / CGC grades in this product. `condition` is real
 *   and renders as a `.ds-tag--abbrev` next to the rarity tag.
 * - Prices are Rands. Pass the `R` prefix in the string, this component never
 *   formats currency and must never be handed a dollar amount.
 * - Tile badges are pills. `availability` renders `.ds-tilebadge--avail`, never
 *   the retired square `.ds-listing__badge`.
 * - Borderless, tone lift only. The card casts no shadow, only the Sheet does.
 *
 * Element choice: the whole card is the navigation target, so it renders a
 * `<button>` (or an `<a>` when `href` is set). The wishlist heart is the one
 * deliberate nested interactive exception and is therefore rendered as a
 * `role="button"` span with `stopPropagation`. When `action` or `quantity` is
 * supplied the root drops to a `<div>`, because a `<button>` inside a
 * `<button>` is invalid HTML, and the card name becomes the link instead.
 */
export const ProductCard = forwardRef(function ProductCard(
  {
    className,

    // Mode and shape.
    mode = 'tile',
    mini = false,
    aspect = 'square',

    // Image.
    image,
    imageAlt = '',

    // Text anatomy, in render order.
    set,
    name,
    rarity,
    language,
    condition,
    cardNumber,
    listingCount,
    fromLabel,
    price,
    /* A SHORT QUALIFIER ON THE PRICE, never a replacement for it. The app
       passes "Price unconfirmed" here for a tracked listing whose market read
       has gone stale (`utils/priceAvailability.js`); the component only knows
       it is a string that belongs beside the figure. Text only, on purpose --
       no colour, icon or badge language is invented for it, because what this
       state should LOOK like is an open design-system decision and must not be
       settled by whoever wired the data. */
    priceNote,
    marketPrice,
    marketLabel = 'Market',
    seller,
    sellerLabel = 'Sold by',
    quantityLabel,

    // Tile chrome.
    availability,
    badges,

    // Action slots.
    wishlist = false,
    wishlisted = false,
    wishlistLabel = 'Save to wishlist',
    onWishlistToggle,
    quantity,
    action,
    /* The tile's own add-to-cart. Distinct from the free-form `action` slot
       below on purpose: `action` is arbitrary content and forces the root to a
       plain div (see `hasNestedControls`), which costs the tile its link. This
       one keeps the tile navigable -- see the `linkedBody` note below. */
    onQuickAdd,
    quickAddLabel = 'Add to cart',
    /* THE ADD IS IN FLIGHT. A real `disabled`, not a label swap: on the store
       this control now fronts a bounded upstream price refresh that can take
       ~24 SECONDS in the worst case (`ONDEMAND_TIMEOUT = 8` in
       `apps/api/apps/catalog/sources/justtcg.py` is a per-phase scalar --
       connect, retried connect, read -- and `fetch_card` can spend it twice;
       the axios instance gives up at 30s). A wait that long with a live button
       is a wait a buyer taps through. */
    quickAddPending = false,
    quickAddPendingLabel = 'Confirming price…',
    inCart = false,

    // Navigation.
    href,
    onClick,

    ...rest
  },
  ref,
) {
  const isTile = mode === 'tile';
  /* A quick-add button counts as a nested control for the same reason `action`
     does: a <button> inside an <a> is interactive content inside a link, which
     is invalid HTML and reaches the button by accident on a screen reader. So
     the root drops to a plain div -- and the card already handles what that
     costs, by promoting the TITLE to the link whenever the root is a div
     (`titleContent` below). The tile keeps opening the card; it is the words
     rather than the whole box that carry the destination. */
  const showQuickAdd = Boolean(onQuickAdd);
  const hasNestedControls = Boolean(quantity || action || showQuickAdd);
  const rootIsButton = !hasNestedControls && Boolean(onClick) && !href;
  const rootIsLink = !hasNestedControls && Boolean(href);
  const Root = rootIsLink ? 'a' : rootIsButton ? 'button' : 'div';

  const showSeller = !isTile && Boolean(seller);
  const showHeart = Boolean(wishlist || onWishlistToggle);
  const heartOnTile = isTile && showHeart;
  const heartInActions = !isTile && showHeart;
  const showActions = Boolean(quantity || action || heartInActions || showQuickAdd);

  // `[hidden]` loses to any rule that sets `display`, so nothing here is hidden
  // by attribute. A slot that has no value is simply not rendered.
  /* TWO PLACEMENTS, ONE PILL — the split the desktop row already makes and
     documents (patterns/market/market.css:120). A tile has room to float the
     badge over the art. A row does not: in `--row` mode the tile is 84px wide
     with `overflow: hidden`, and "12 listings" at 12px semibold needs ~97px
     plus the badge's 8px inset, so it was cut off mid-word against a hard edge.
     On a row the badge joins the meta line instead, leading it — availability
     is the fact that decides whether the rest of the line is worth reading. */
  const availInTile = isTile && Boolean(availability);
  const availInMeta = !isTile && Boolean(availability);

  const hasMetaRow = Boolean(availInMeta || rarity || language || condition || cardNumber);
  const hasListings = listingCount !== undefined && listingCount !== null;
  /* `priceNote` counts. It qualifies the figure, so a caller that has a note
     and (somehow) no figure still gets the qualification rather than silence
     -- the one thing this row may never do is drop the note and keep quiet. */
  const hasPriceRow = hasListings || Boolean(price) || Boolean(priceNote);

  const quickAddWords = quickAddText({
    pending: quickAddPending,
    pendingLabel: quickAddPendingLabel,
    inCart,
    label: quickAddLabel,
  });

  const tile = (
    <span
      className={cn('ds-pcard__tile', TILE_ASPECT[aspect])}
      // The tile is the parent that `.ds-pcard__badges` and `.ds-heart`
      // position against, which is why both live inside it and not beside it.
    >
      {typeof image === 'string' ? <img src={image} alt={imageAlt} /> : image}

      {(availInTile || badges) && (
        <span className="ds-pcard__badges">
          {availInTile && (
            <span className="ds-tilebadge ds-tilebadge--avail">{availability}</span>
          )}
          {badges}
        </span>
      )}

      {heartOnTile && (
        <WishlistHeart
          asSpan={Root === 'button' || Root === 'a'}
          pressed={wishlisted}
          label={wishlistLabel}
          onToggle={onWishlistToggle}
        />
      )}
    </span>
  );

  const titleContent =
    Root === 'div' && (href || onClick) ? (
      href ? (
        <a className="ds-pcard__title ds-pcard__title--link" href={href}>
          {name}
        </a>
      ) : (
        <button type="button" className="ds-pcard__title ds-pcard__title--link" onClick={onClick}>
          {name}
        </button>
      )
    ) : (
      <h3 className="ds-pcard__title">{name}</h3>
    );

  // A <div>, not a <span>. The text anatomy is real <p> / <h3> markup, because
  // `.ds-pcard__kicker` and friends carry vertical margins that an inline box
  // would swallow. The card root stays a <button> per the CSS's own button
  // reset, which is the library's long standing shape for this block.
  const body = (
    <div className="ds-pcard__body">
      {set && <p className="ds-pcard__kicker">{set}</p>}

      {titleContent}

      {hasMetaRow && (
        <div className="ds-pcard__metarow">
          {availInMeta && (
            <span className="ds-tilebadge ds-tilebadge--avail">{availability}</span>
          )}
          {rarity && <span className="ds-tag ds-tag--rarity">{rarity}</span>}
          {/* Language is a catalogue fact, not a condition. It is its own pill
              so a Japanese print is never mistaken for a grade or a state. */}
          {language && <span className="ds-tag ds-tag--rarity">{language}</span>}
          {condition && <span className="ds-tag ds-tag--abbrev">{condition}</span>}
          {cardNumber && <span className="ds-pcard__code">{cardNumber}</span>}
        </div>
      )}

      {hasPriceRow && (
        <p className="ds-pcard__listings">
          {hasListings && (
            <span className="ds-pcard__from">
              {fromLabel || `${listingCount} ${listingCount === 1 ? 'listing' : 'listings'} from`}
            </span>
          )}
          {price && <span className="ds-pcard__pricemain">{price}</span>}
          {/* AFTER the figure, never instead of it. Reading order is the
              qualification too: "R764.15, Price unconfirmed" is a figure with
              a caveat, where the reverse reads as a heading over a number. */}
          {priceNote && <span className="ds-pcard__pricenote">{priceNote}</span>}
        </p>
      )}

      {/* Only when it ADDS something — same rule as the desktop tile. A caller
          with no listing minimum falls back to the reference price for both
          slots, and the card then printed one number twice. */}
      {marketPrice && marketPrice !== price && (
        <p className="ds-pcard__market">
          {marketLabel} <span className="ds-pcard__market-value">{marketPrice}</span>
        </p>
      )}

      {showSeller && (
        <p className="ds-pcard__seller">
          {sellerLabel} {seller}
        </p>
      )}

      {quantityLabel && <p className="ds-pcard__market">{quantityLabel}</p>}
    </div>
  );

  return (
    <Root
      ref={ref}
      {...(Root === 'button' ? { type: 'button', onClick } : null)}
      {...(Root === 'a' ? { href, onClick } : null)}
      className={cn(
        'ds-pcard',
        mini && isTile && 'ds-pcard--mini',
        mode === 'row' && 'ds-pcard--row',
        mode === 'compact' && 'ds-pcard--compact',
        className,
      )}
      {...rest}
    >
      {tile}
      {body}
      {showActions && (
        <div className="ds-pcard__actions">
          {quantity}
          {action}
          {showQuickAdd && (
            <button
              type="button"
              className="ds-pcard__quickadd"
              /* A REAL GATE, not a label swap. On the seller store this button
                 fronts a cart write that is no longer synchronous
                 (`hooks/useAddToCart.js`, "THE AWAIT BREAKS THE CART'S
                 ATOMICITY"). It is an ADDITIONAL gate rather than a
                 replacement: the hook keeps its own synchronous per-listing
                 claim, which outlives this one whenever a dialog is left open,
                 and that claim -- not this -- is the guarantee.

                 `aria-disabled`, NOT `disabled` (fix round 1). A real
                 `disabled` leaves the tab order, so the buyer who just
                 activated this button loses focus to `<body>`: the relabel to
                 "Confirming price…" is never announced, `aria-busy` on an
                 unfocused control is not reliably spoken, and after a wait that
                 can run ~24s they have to tab from the top of the grid again.
                 On the two dialog outcomes the dialog traps and restores focus;
                 on the SILENT-ADD outcome nothing would speak at all. Keeping
                 the button focusable keeps the name change announced, and the
                 early return below keeps the gate. */
              aria-disabled={quickAddPending || undefined}
              aria-busy={quickAddPending || undefined}
              aria-label={quickAddAccessibleName({
                text: quickAddWords,
                note: priceNote,
                pending: quickAddPending,
              })}
              onClick={(event) => {
                /* The art and the title above are a link, so the add has to
                   stop here or every add is also a navigation. Both run before
                   the pending check: a tap on a busy button must still not
                   navigate. */
                event.preventDefault();
                event.stopPropagation();
                if (quickAddPending) return;
                onQuickAdd(event);
              }}
            >
              {quickAddWords}
            </button>
          )}
          {heartInActions && (
            <WishlistHeart
              pressed={wishlisted}
              label={wishlistLabel}
              onToggle={onWishlistToggle}
            />
          )}
        </div>
      )}
    </Root>
  );
});

export default ProductCard;
